import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  CardHeader,
  Divider,
  InputAdornment,
  LinearProgress
} from "@mui/material";
import { 
  TrendingUp, 
  LocalHospital, 
  CalendarMonth,
  Sort,
  FilterList,
  Analytics,
  ShowChart,
  BarChart
} from "@mui/icons-material";
import { getLinenWardConsumption } from "../../services/api";
import { useSnackbar } from "notistack";

export const LinenWardConsumption = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState([]);
  const [totalConsumption, setTotalConsumption] = useState(0);
  const [sortBy, setSortBy] = useState("total");
  const [sortOrder, setSortOrder] = useState("desc");

  // Load data on component mount with default dates
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    // Validate dates
    if (new Date(from) > new Date(to)) {
      enqueueSnackbar("❌ End date cannot be before start date", { variant: "error" });
      return;
    }

    try {
      setLoading(true);
      const res = await getLinenWardConsumption(from, to);
      
      if (res.data && res.data.success) {
        const usageData = res.data.usage || [];
        setUsage(usageData);
        
        // Calculate total consumption
        const total = usageData.reduce((sum, ward) => sum + (ward.total || 0), 0);
        setTotalConsumption(total);
        
        enqueueSnackbar(`🎉 Loaded data for ${usageData.length} wards`, { variant: "success" });
      } else {
        throw new Error(res.data?.message || "Failed to load data");
      }
    } catch (err) {
      console.error("Load consumption error:", err);
      enqueueSnackbar(err.message || "❌ Failed to load consumption data", { variant: "error" });
      setUsage([]);
      setTotalConsumption(0);
    } finally {
      setLoading(false);
    }
  };

  // Sort usage data
  const sortedUsage = [...usage].sort((a, b) => {
    const aValue = a[sortBy] || 0;
    const bValue = b[sortBy] || 0;
    
    if (sortOrder === "desc") {
      return bValue - aValue;
    } else {
      return aValue - bValue;
    }
  });

  // Get top consuming ward
  const topWard = sortedUsage.length > 0 ? sortedUsage[0] : null;

  // Format ward name for display
  const formatWardName = (wardId) => {
    if (!wardId) return "Unknown Ward";
    
    // If wardId is an object with name property
    if (typeof wardId === 'object' && wardId.name) {
      return wardId.name;
    }
    
    // If it's just an ID string, you might want to format it
    if (typeof wardId === 'string') {
      // Remove any ObjectId prefixes and take first 8 chars
      return wardId.replace(/^[a-f0-9]{24}$/, '').slice(-8) || 'Ward';
    }
    
    return wardId.toString();
  };

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header Section */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Analytics sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Ward Consumption Report
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Track linen usage across hospital wards
              </Typography>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                startIcon={<TrendingUp sx={{ fontSize: 28 }} />}
                onClick={loadData}
                disabled={loading}
                sx={{
                  backgroundColor: 'white',
                  color: 'primary.main',
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  '&:hover': {
                    backgroundColor: 'grey.100',
                    transform: 'translateY(-2px)',
                    boxShadow: 3
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? 'Loading...' : 'Refresh Data'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filters Card */}
      <Card sx={{ 
        mb: 4, 
        borderRadius: 3, 
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
      }}>
        <CardHeader
          title={
            <Typography variant="h4" fontWeight={700} sx={{ fontSize: '1.6rem' }}>
              📅 Report Period Settings
            </Typography>
          }
          subheader={
            <Typography sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
              Select date range and sorting preferences
            </Typography>
          }
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'grey.50'
          }}
        />
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="From Date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                fullWidth
                size="medium"
                InputLabelProps={{ 
                  shrink: true,
                  sx: { fontSize: '1.1rem' }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarMonth sx={{ color: "primary.main", fontSize: 28 }} />
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '1.1rem',
                    '& input': {
                      padding: '12px 14px',
                      fontSize: '1.1rem'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                type="date"
                label="To Date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                fullWidth
                size="medium"
                InputLabelProps={{ 
                  shrink: true,
                  sx: { fontSize: '1.1rem' }
                }}
                InputProps={{
                  sx: {
                    fontSize: '1.1rem',
                    '& input': {
                      padding: '12px 14px',
                      fontSize: '1.1rem'
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontSize: '1.1rem' }}>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => setSortBy(e.target.value)}
                  sx={{ fontSize: '1.1rem' }}
                  startAdornment={
                    <InputAdornment position="start">
                      <Sort sx={{ color: 'primary.main', fontSize: 28 }} />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="total" sx={{ fontSize: '1.1rem' }}>Total Usage</MenuItem>
                  <MenuItem value="wardName" sx={{ fontSize: '1.1rem' }}>Ward Name</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="medium">
                <InputLabel sx={{ fontSize: '1.1rem' }}>Order</InputLabel>
                <Select
                  value={sortOrder}
                  label="Order"
                  onChange={(e) => setSortOrder(e.target.value)}
                  sx={{ fontSize: '1.1rem' }}
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterList sx={{ color: 'primary.main', fontSize: 28 }} />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="desc" sx={{ fontSize: '1.1rem' }}>Descending</MenuItem>
                  <MenuItem value="asc" sx={{ fontSize: '1.1rem' }}>Ascending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button 
                variant="contained" 
                onClick={loadData}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={24} /> : <ShowChart sx={{ fontSize: 28 }} />}
                sx={{ 
                  fontSize: '1.2rem',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  borderRadius: 3,
                  minHeight: '56px',
                  minWidth: '100%'
                }}
              >
                {loading ? 'Loading...' : "Generate"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {!loading && usage.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <LocalHospital sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 1 }}>
                  {usage.length}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
                  Total Wards
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '1rem', opacity: 0.8 }}>
                  Active wards in period
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white'
            }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <TrendingUp sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ fontSize: '2.5rem', fontWeight: 800, mb: 1 }}>
                  {totalConsumption}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
                  Total Consumption
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '1rem', opacity: 0.8 }}>
                  Items consumed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white'
            }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Avatar sx={{ backgroundColor: 'rgba(255,255,255,0.2)', mb: 2, mx: 'auto' }}>
                  <BarChart sx={{ fontSize: 32, color: 'white' }} />
                </Avatar>
                <Typography variant="h2" sx={{ 
                  fontSize: '1.8rem', 
                  fontWeight: 800, 
                  mb: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {topWard ? formatWardName(topWard._id) : 'N/A'}
                </Typography>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', opacity: 0.9 }}>
                  Top Consumer
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '1rem', opacity: 0.8 }}>
                  {topWard ? `${topWard.total} items` : 'No data'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          my: 8, 
          flexDirection: 'column',
          gap: 3
        }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
            Loading Consumption Data...
          </Typography>
          <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
            Analyzing ward usage patterns
          </Typography>
        </Box>
      )}

      {/* Data Table */}
      {!loading && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Analytics sx={{ fontSize: 32, color: 'primary.main' }} />
                <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                  Ward Consumption Details
                </Typography>
                <Chip 
                  label={usage.length} 
                  color="primary" 
                  size="medium"
                  sx={{ fontSize: '1.1rem', fontWeight: 600, minWidth: 60 }}
                />
              </Box>
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50'
            }}
          />
          <CardContent>
            {usage.length === 0 ? (
              <Alert 
                severity="info" 
                sx={{ 
                  fontSize: '1.1rem',
                  borderRadius: 3,
                  '& .MuiAlert-message': { fontSize: '1.1rem' }
                }}
              >
                No consumption data found for the selected period. Try adjusting the date range.
              </Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead sx={{ backgroundColor: 'primary.main' }}>
                    <TableRow>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        🏥 Ward Name
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📊 Total Consumption
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📈 Percentage
                      </TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                        📦 Items
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedUsage.map((ward, index) => {
                      const percentage = totalConsumption > 0 
                        ? ((ward.total / totalConsumption) * 100).toFixed(1)
                        : 0;
                      
                      return (
                        <TableRow 
                          key={ward._id} 
                          hover
                          sx={{ 
                            '&:last-child td, &:last-child th': { border: 0 },
                            backgroundColor: index === 0 ? 'warning.light' : 'inherit',
                            '&:hover': {
                              backgroundColor: index === 0 ? 'warning.light' : 'primary.light',
                              transform: 'translateY(-1px)',
                              transition: 'all 0.3s ease'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  fontSize: '1.1rem', 
                                  fontWeight: index === 0 ? 800 : 600 
                                }}
                              >
                                {formatWardName(ward._id)}
                              </Typography>
                              {index === 0 && (
                                <Chip 
                                  label="🏆 Top Consumer" 
                                  color="warning" 
                                  size="medium"
                                  sx={{ fontSize: '1rem', fontWeight: 600 }}
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="center">
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontSize: '1.3rem', 
                                fontWeight: index === 0 ? 800 : 700,
                                color: index === 0 ? 'warning.main' : 'primary.main'
                              }}
                            >
                              {ward.total || 0}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={`${percentage}%`} 
                                size="medium"
                                color={
                                  percentage > 20 ? 'primary' : 
                                  percentage > 10 ? 'secondary' : 'default'
                                }
                                variant={percentage > 10 ? 'filled' : 'outlined'}
                                sx={{ fontSize: '1.1rem', fontWeight: 600, minWidth: 80 }}
                              />
                              <LinearProgress 
                                variant="determinate" 
                                value={Math.min(percentage, 100)} 
                                sx={{ 
                                  width: '100%', 
                                  height: 8, 
                                  borderRadius: 4,
                                  backgroundColor: 'grey.200',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: percentage > 20 ? 'primary.main' : 
                                                    percentage > 10 ? 'secondary.main' : 'grey.400'
                                  }
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            {ward.items && Array.isArray(ward.items) ? (
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                                {ward.items.length} item types
                              </Typography>
                            ) : (
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                                Details not available
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* Report Period Info */}
      {!loading && usage.length > 0 && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary', fontWeight: 600 }}>
            📅 Report Period: {new Date(from).toLocaleDateString()} to {new Date(to).toLocaleDateString()}
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', color: 'text.secondary', mt: 1 }}>
            Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
          </Typography>
        </Box>
      )}
    </Box>
  );
};